import { computed, reactive, watch } from 'vue'
import * as Sentry from '@sentry/vue'
import { useIdle, useTimestamp } from '@vueuse/core'
import { type Session as SharedSession, sessionState } from '@klausapp/state/session'

import { setWeekStart } from '@/utils/date'
import type { Session } from '@/types/session'
import config, { embeddedRuntime } from '@/config'
import { APIError, api, zendeskApi } from '@/api'
import { logout } from '@/utils/logout'
import { identify } from '@/utils/analytics'
import { applyDefaults } from '@/utils/constants/default-workspace-settings'
import { getSupportedLocale } from '@/i18n'

/**
 * Session reactive object
 */
export const session = reactive<Session>({
  features: {},
  account: undefined,
  user: undefined,
  workspace: undefined,
  system: undefined,
  isAuthenticated: false,
  fullPageRedirectFromError: false,
  csrfToken: undefined,
  arturoFeatures: undefined,
} as any)

export const isArturoFlagEnabled = (key?: string | null): boolean => {
  if (!key) return false
  return session.arturoFeatures?.[key] === true
}

const checkZendeskManageGroups = async (): Promise<boolean> => {
  try {
    const { roles } = await zendeskApi
      .get('v2/users/me.json', { searchParams: { include: 'abilities,roles' } })
      .json<{ roles?: { name: string; configuration?: Record<string, unknown> }[] }>()
    const adminRole = roles?.find((r) => r.name === 'Admin')
    return Boolean(adminRole?.configuration?.manage_groups)
  } catch {
    return false
  }
}

/**
 * Flatten the Vue session into the framework-agnostic read-model shape.
 *
 * Exported for testing: `toSharedSession` typechecks the shape, but nothing
 * typechecks that each field is copied from the right place, so the value
 * mapping is isolated here rather than inlined into `loadSession`.
 */
export const toSharedSession = (source: Session): SharedSession => {
  const { features, workspace, account, user } = source

  return {
    // Rebuild rather than cast: `Features` is an interface, so it has no index
    // signature and does not satisfy Record<string, boolean>. Rebuilding also
    // snapshots the flags, which matters because the shared state treats them as
    // frozen at boot — handing over the live reactive object would let them mutate under it.
    features: Object.fromEntries(Object.entries(features)),
    workspace: workspace
      ? {
          id: workspace.id,
          role: workspace.role,
          highestRole: workspace.highestRole,
        }
      : null,
    account: account
      ? {
          id: account.id,
          role: account.role,
          customSearchFieldId: account.customSearchFieldId,
          reasoningEnabled: account.reasoningEnabled,
        }
      : null,
    user: user ? { id: user.id } : null,
  }
}

/**
 * Load all necessary session metadata for app boot
 */
export const loadSession = async () => {
  // Assume some session when behind Zorg, unless we are on the legacy domain
  if (config.baseUrl === '/qa' && !location.hostname.endsWith(config.legacyDomainRoot)) {
    session.isAuthenticated = true
  } else {
    return session
  }

  try {
    const sessionRequest = api.get('users/session', { searchParams: contextSwitchParams() }).json<any>()

    const zendeskFeaturesRequest = zendeskApi
      .get('v2/account/features')
      .json<{ features?: Record<string, { enabled?: boolean }> }>()
      .catch(() => undefined)

    // Load session metadata and ZOS-only Zendesk feature flags in parallel.
    const [
      { user, account, workspace, features, csrfToken, arturoFeatures: sessionArturoFeatures },
      zdFeaturesResponse,
      manageGroups,
    ] = await Promise.all([sessionRequest, zendeskFeaturesRequest, checkZendeskManageGroups()])

    const zendeskArturoFeatures: Record<string, boolean> = {}
    const zdFeatures: Record<string, { enabled?: boolean }> = zdFeaturesResponse?.features ?? {}

    for (const [key, value] of Object.entries(zdFeatures)) {
      if (value?.enabled !== undefined) zendeskArturoFeatures[key] = value.enabled
    }
    session.arturoFeatures = { ...sessionArturoFeatures, ...zendeskArturoFeatures }

    if (features) {
      const unifiedNavigationAvailable = Boolean(zdFeaturesResponse?.features?.unified_navigation_available?.enabled)
      if (unifiedNavigationAvailable) features.zendeskUiNavigation = true

      // Hide global nav GA banner when GA hits the account
      const unifiedNavigationGAFullRollout = Boolean(
        zdFeaturesResponse?.features?.unified_navigation_full_rollout?.enabled,
      )
      if (unifiedNavigationGAFullRollout) features.zendeskUiReleaseReminderBanner = false
    }

    if (account) {
      session.user = user
      session.user.id = parseInt(user.id, 10)
      session.user.settings = session.user.settings || {}
      session.account = account
      session.account.id = parseInt(account.id, 10)

      session.features = features
      session.zendesk = { manageGroups }

      if (workspace) {
        session.workspace = workspace
        session.workspace.id = parseInt(workspace.id, 10)
        session.workspace.settings = applyDefaults(session.workspace.settings)
      }

      if (csrfToken) session.csrfToken = csrfToken

      // Copy login info into shared state that consumers outside this Vue app
      // can read (a separate bundle cannot reach into the Vue session object).
      // These values never change after login, so a one-time copy is safe.
      // TODO: values that change at runtime are handled later.
      Object.assign(sessionState, toSharedSession(session))

      // Redirect to main subdomain if integrated account is visited from brand subdomain
      const currentSubdomain = location.hostname.split('.')[0]
      if (currentSubdomain !== session.account.subdomain) {
        location.href = location.href.replace(location.host, `${session.account.subdomain}.${config.zendeskDomain}`)
        session.fullPageRedirectFromError = true
        return session
      }

      setWeekStart(session.user.firstDayOfWeek) // Set day.js first day of the week

      identify(session)

      // Set user information, as well as tags and further extras
      const scope = Sentry.getCurrentScope()
      scope.setTag('account_role', session.account.role)
      scope.setTag('workspace_role', session.workspace?.role)
      scope.setUser({ id: session.user.id.toString(), email: session.user.email })
    }
  } catch (err: unknown) {
    if (!(err instanceof APIError)) throw err

    // Handle logged out Zendesk users & invalid account state behind proxy
    if (err.status === 401) {
      session.fullPageRedirectFromError = true
      handleExpiredSession()
      return session
    } else if (err.status === 403 && err.body.includes('does not have QA product')) {
      session.fullPageRedirectFromError = true
      redirectToMarketingPage()
      return session
    } else if (err.status === 403) {
      session.fullPageRedirectFromError = true
      location.href = '/'
      return session
    } else if (err.status === 404 && err.body.includes('account is not yet migrated')) {
      history.replaceState(null, '', '/qa/account-not-found/not-migrated')
      return session
    } else if (err.status === 404 && err.body.includes('account not found')) {
      history.replaceState(null, '', '/qa/account-not-found')
      return session
    } else if (err.status === 409) {
      history.replaceState(null, '', '/qa/account-locked')
      return session
    } else {
      throw err
    }
  }

  // Start Zendesk session renewal
  if (session.isAuthenticated) {
    let expiresAt = await renewZendeskSession()

    const BUFFER_SECONDS = 120 // 2 minute buffer before expiration
    const { idle } = useIdle(60 * 1000)
    const now = useTimestamp({ interval: 1000 })
    const expirySeconds = computed(() => Math.floor((expiresAt - now.value) / 1000))

    watch(expirySeconds, async (secondsLeft) => {
      if (idle.value && secondsLeft <= 0) {
        handleExpiredSession() // Logout idle users when session has expired
      } else if (!idle.value && secondsLeft <= BUFFER_SECONDS) {
        expiresAt = await renewZendeskSession() // Renew session of active user when buffer is reached
      }
    })

    watch(idle, async (idleValue) => {
      // Immediately renew session when coming out of idle state
      if (!idleValue) expiresAt = await renewZendeskSession()
    })
  }

  return session
}

const renewZendeskSession = async () => {
  try {
    const res = await zendeskApi
      .extend({ hooks: { afterResponse: undefined } })
      .extend({ hooks: { afterResponse: [] } })
      .get('v2/users/me/session/renew.json')
    return Number(res.headers.get('X-Zendesk-User-Session-Expires-At') || 0) * 1000
  } catch (err: unknown) {
    if (err instanceof APIError && err.status === 401) {
      handleExpiredSession()
    } else {
      throw err
    }
  }
}

const handleExpiredSession = () => {
  if (embeddedRuntime()) {
    logout()
  } else {
    location.href = `/access/login?return_to=${encodeURIComponent((embeddedRuntime() ? '' : location.origin) + location.pathname + location.search)}&role=agent`
  }
}

const contextSwitchParams = () => {
  const url = new URL(window.location.href)
  const response: { accountId?: number; workspaceId?: number; locale?: string } = {}
  const account = url.searchParams.get('account')
  const workspace = url.searchParams.get('workspace')
  const locale = getSupportedLocale(url.searchParams.get('lang') || '')

  if (!account && !workspace && !locale) return
  if (account) response.accountId = parseInt(account, 10)
  if (workspace) response.workspaceId = parseInt(workspace, 10)
  if (locale) response.locale = locale

  url.searchParams.delete('account')
  url.searchParams.delete('workspace')
  url.searchParams.delete('lang')

  history.replaceState(null, '', url.toString())

  return response
}

export const redirectToMarketingPage = () => {
  location.href = 'https://zendesk.com/qa'
}
